import { useState } from 'react';
import { imagePickSize } from 'utils/general';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import DOMPurify from 'dompurify';
import Avatar from 'components/Avatar';
import { useAutosizeTextarea } from 'hooks/useAutosizeTextarea';
import { useFloatingTooltip } from 'hooks/useFloatingTooltip';
import CommentOptions from './CommentOptions';

import './styles.scss';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const TaskComments = ({
  comments,
  addComment,
  editComment,
  deleteComment,
  timeZone,
  userId,
  dateFormat = 'MMM D, YYYY',
  timeFormat = 'h:mm A',
}) => {
  const [newComment, setNewComment] = useState('');
  const [editModeId, setEditModeId] = useState(null);
  const [editedText, setEditedText] = useState('');
  const [tooltipMessage, setTooltipMessage] = useState('');

  const { textareaRef } = useAutosizeTextarea({ extraHeight: 30 });

  const {
    showTooltip,
    hideTooltip,
    updateTooltip,
    tooltipElement,
  } = useFloatingTooltip(tooltipMessage, { position: 'bottom' });

  const handleSubmit = () => {
    const sanitized = DOMPurify.sanitize(newComment.trim());
    if (sanitized) {
      addComment(sanitized);
      setNewComment('');
    }
  };

  const handleEdit = (comment) => {
    setEditModeId(comment.id);
    setEditedText(comment.body);
  };

  const handleSave = async (commentId, originalBody) => {
    const sanitized = DOMPurify.sanitize(editedText.trim());
    if (!sanitized) return;

    if (sanitized === DOMPurify.sanitize(originalBody.trim())) {
      setEditModeId(null);
      setEditedText('');
      setNewComment('');
      return;
    }

    editComment(commentId, sanitized);
    setEditModeId(null);
    setEditedText('');
    setNewComment('');
  };

  const handleCancel = () => {
    setEditModeId(null);
    setEditedText('');
    setNewComment('');
  };

  return (
    <>
      <div className="comment-container">
        <label htmlFor="comments">comments</label>
        <div className="comments__textarea-wrapper">
          <textarea
            ref={textareaRef}
            name="comments"
            className="comments__textarea"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment"
          />
          <button
            className="comments__submit button"
            onClick={handleSubmit}
          >
            Comment
          </button>
        </div>
      </div>
      <div className="comments-list">
        {comments.map(comment => {
          const { profileImage, firstname, lastname, initials } = comment.login || {};
          let imagePath = null;
          if (profileImage) {
            imagePath = `//${process.env.REACT_APP_AWS_CLOUDFRONT}/${imagePickSize(
              profileImage,
              'square60',
            )}`;
          }
          const isEdited = !!comment.updatedAt;
          const timestamp = isEdited ? comment.updatedAt : comment.createdAt;

          const isEditing = editModeId === comment.id;

          return (
            <div
              key={`comment-${comment.id}`}
              className="lead-task-comment"
            >
              <div className="lead-task-comment__image">
                <Avatar
                  photo={imagePath}
                  text={!imagePath ? initials : null}
                  size={30}
                  borderSize={1}
                />
              </div>
              <div className="lead-task-comment__content">
                <div className="lead-task-comment__head">
                  <div className="lead-task-comment__name">
                    {firstname} {lastname}
                  </div>
                  <div
                    className="lead-task-comment__date"
                    onMouseEnter={e => {
                      const date = dayjs(timestamp ?? '')
                        .tz(timeZone)
                        .format(`${dateFormat} ${timeFormat} z`);
                      showTooltip();
                      updateTooltip(e);
                      setTooltipMessage(date);
                    }}
                    onMouseMove={e => updateTooltip(e)}
                    onMouseLeave={hideTooltip}
                  >
                    {isEdited && '(Edited) '}
                    {dayjs(timestamp).fromNow()}
                    {tooltipElement}
                  </div>
                  {comment.loginId === userId ? (
                    <div className="lead-task-comment__options">
                      <CommentOptions
                        commentId={comment.id}
                        onEdit={() => handleEdit(comment)}
                        onDelete={() => deleteComment(comment.id)}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="lead-task-comment__body">
                  {isEditing ? (
                    <>
                      <textarea
                        value={editedText}
                        onChange={(e) => setEditedText(e.target.value)}
                      />
                      <div className="lead-task-comment__edit-buttons">
                        <button
                          onClick={(e) =>{
                            e.stopPropagation();
                            handleSave(comment.id, comment.body);
                          }}
                          className="button button--md"
                        >
                          Save
                        </button>
                        <button
                          onClick={(e) =>{
                            e.stopPropagation();
                            handleCancel();
                          }}
                          className="button button--outline button--md"
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    comment.body
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default TaskComments;
